import React, { useEffect, useMemo, useState } from "react";
import { endpoints } from "@/lib/api";
import { Play, X, Check, Pause, SkipForward, Plus, ListChecks, GripVertical } from "lucide-react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { CategoryBadge } from "@/components/Badges";
import { formatDate } from "@/lib/utils-fos";
import { toast } from "sonner";

export default function SOPs() {
  const [tab, setTab] = useState("library");
  const [sops, setSops] = useState([]);
  const [runs, setRuns] = useState([]);
  const [selected, setSelected] = useState(null);
  const [steps, setSteps] = useState([]);
  const [runContext, setRunContext] = useState(null); // sop to run
  const [selectedRun, setSelectedRun] = useState(null);
  const [runStepsData, setRunStepsData] = useState([]);
  const [showCreate, setShowCreate] = useState(false);

  const load = async () => {
    const [ss, rr] = await Promise.all([endpoints.sops(), endpoints.sopRuns()]);
    setSops(ss);
    setRuns(rr);
    if (ss[0] && !selected) selectSop(ss[0]);
  };
  useEffect(() => {
    load();
  }, []);

  const selectSop = async (s) => {
    setSelected(s);
    const st = await endpoints.sopSteps(s.id);
    setSteps(st);
  };

  const onStepDragEnd = async (result) => {
    if (!result.destination || result.source.index === result.destination.index) return;
    const newSteps = Array.from(steps);
    const [moved] = newSteps.splice(result.source.index, 1);
    newSteps.splice(result.destination.index, 0, moved);
    const renumbered = newSteps.map((s, i) => ({ ...s, step_number: i + 1 }));
    setSteps(renumbered);
    await endpoints.reorderSopSteps(selected.id, renumbered.map((s) => s.id));
  };

  const runCounts = useMemo(() => {
    const map = {};
    runs.forEach((r) => (map[r.sop_id] = (map[r.sop_id] || 0) + 1));
    return map;
  }, [runs]);

  const openRun = async (r) => {
    setSelectedRun(r);
    const rs = await endpoints.sopRunSteps(r.id);
    setRunStepsData(rs);
  };

  const setStepStatus = async (step, status) => {
    const patch = {
      status,
      completed_at: status === "done" ? new Date().toISOString() : null,
    };
    await endpoints.updateSopRunStep(step.id, patch);
    setRunStepsData(runStepsData.map((s) => (s.id === step.id ? { ...s, ...patch } : s)));
    // maybe complete run if all done
    const allDone = runStepsData.every((s) => (s.id === step.id ? status === "done" : s.status === "done"));
    if (allDone && selectedRun) {
      await endpoints.updateSopRun(selectedRun.id, { status: "complete", completed_at: new Date().toISOString() });
      setSelectedRun({ ...selectedRun, status: "complete" });
      setRuns(runs.map((r) => (r.id === selectedRun.id ? { ...r, status: "complete" } : r)));
      toast.success("SOP run complete!");
    }
  };

  const pauseCancel = async (run, status) => {
    await endpoints.updateSopRun(run.id, { status });
    setRuns(runs.map((r) => (r.id === run.id ? { ...r, status } : r)));
    setSelectedRun({ ...run, status });
  };

  const activeRuns = runs.filter((r) => r.status !== "complete" && r.status !== "cancelled");
  const completedRuns = runs.filter((r) => r.status === "complete" || r.status === "cancelled");

  return (
    <div className="p-4 md:p-8 pb-24 md:pb-8 max-w-7xl mx-auto">
      <div className="flex items-center gap-2 mb-6 border-b border-neutral-200">
        <TabBtn active={tab === "library"} onClick={() => setTab("library")} testid="tab-library">Library</TabBtn>
        <TabBtn active={tab === "runs"} onClick={() => setTab("runs")} testid="tab-runs">
          Active Runs
          {activeRuns.length > 0 && (
            <span className="ml-1.5 text-[10px] mono px-1.5 py-0.5 rounded bg-neutral-900 text-white">
              {activeRuns.length}
            </span>
          )}
        </TabBtn>
      </div>

      {tab === "library" && (
        <div className="grid grid-cols-1 lg:grid-cols-[380px,1fr] gap-4">
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs mono font-semibold text-neutral-500">SOP LIBRARY</h3>
              <button onClick={() => setShowCreate(true)} className="text-xs px-2 py-1 rounded-md flex items-center gap-1 bg-neutral-900 text-white hover:bg-neutral-800 transition-colors" data-testid="new-sop-btn">
                <Plus size={12} /> New SOP
              </button>
            </div>
            <div className="space-y-2">
              {sops.map((s) => (
                <div
                  key={s.id}
                  data-testid={`sop-card-${s.id}`}
                  className={`fos-card fos-hover p-3 cursor-pointer transition-colors ${selected?.id === s.id ? "border-neutral-900" : ""}`}
                  onClick={() => selectSop(s)}
                >
                  <div className="flex items-center justify-between mb-1.5">
                    <p className="text-sm font-semibold">{s.name}</p>
                    <CategoryBadge category={s.category} />
                  </div>
                  <div className="flex items-center flex-wrap gap-1 mb-2">
                    {(s.trigger_keywords || []).map((kw, i) => (
                      <span key={i} className="text-[10px] mono px-1.5 py-0.5 rounded bg-neutral-100 text-neutral-500">
                        #{kw}
                      </span>
                    ))}
                  </div>
                  <div className="flex items-center justify-between text-[10px] mono text-neutral-500">
                    <span>{(runCounts[s.id] || 0)} runs</span>
                    <button
                      onClick={(e) => { e.stopPropagation(); setRunContext(s); }}
                      className="px-2 py-1 rounded-md flex items-center gap-1 bg-neutral-900 text-white hover:bg-neutral-800 transition-colors"
                      data-testid={`run-now-${s.id}`}
                    >
                      <Play size={11} /> Run Now
                    </button>
                  </div>
                </div>
              ))}
              {sops.length === 0 && <p className="text-xs italic text-neutral-400">No SOPs yet. Create one to get started.</p>}
            </div>
          </div>

          <div>
            {selected ? (
              <div className="fos-card p-4 animate-fade-in">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div>
                    <h2 className="text-lg font-semibold">{selected.name}</h2>
                    <p className="text-sm mt-1 text-neutral-500">{selected.description || "—"}</p>
                  </div>
                  <button
                    onClick={() => setRunContext(selected)}
                    className="text-sm px-3 py-2 rounded-md flex items-center gap-1.5 bg-neutral-900 text-white hover:bg-neutral-800 transition-colors"
                  >
                    <Play size={14} /> Run this SOP
                  </button>
                </div>
                <div className="mt-4">
                  <p className="text-[10px] mono mb-2 text-neutral-500">TRIGGER KEYWORDS</p>
                  <div className="flex flex-wrap gap-1.5">
                    {(selected.trigger_keywords || []).map((kw, i) => (
                      <span key={i} className="text-xs px-2 py-1 rounded-md bg-neutral-100 text-neutral-800">
                        #{kw}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="mt-4">
                  <p className="text-[10px] mono mb-2 text-neutral-500">STEPS ({steps.length}) · DRAG TO REORDER</p>
                  <DragDropContext onDragEnd={onStepDragEnd}>
                    <Droppable droppableId="sop-steps">
                      {(provided) => (
                        <ol className="space-y-2" ref={provided.innerRef} {...provided.droppableProps}>
                          {steps.map((st, idx) => (
                            <Draggable key={st.id} draggableId={st.id} index={idx}>
                              {(prov, snapshot) => (
                                <li
                                  ref={prov.innerRef}
                                  {...prov.draggableProps}
                                  className={`p-3 rounded-md fos-card ${snapshot.isDragging ? "shadow-lg" : ""}`}
                                  style={prov.draggableProps.style}
                                  data-testid={`sop-step-${st.id}`}
                                >
                                  <div className="flex items-center gap-2 mb-1">
                                    <span {...prov.dragHandleProps} className="cursor-grab text-neutral-400" data-testid={`sop-step-handle-${st.id}`}>
                                      <GripVertical size={13} />
                                    </span>
                                    <span className="text-[10px] mono px-1.5 py-0.5 rounded bg-neutral-100 text-neutral-500">
                                      {String(st.step_number).padStart(2, "0")}
                                    </span>
                                    <span className="text-sm font-medium">{st.title}</span>
                                  </div>
                                  <div className="flex items-center gap-3 text-[10px] mono pl-5 text-neutral-500">
                                    {st.owner_role && <span>OWNER · {st.owner_role.toUpperCase()}</span>}
                                    {st.estimated_hours && <span>~{st.estimated_hours}h</span>}
                                  </div>
                                </li>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </ol>
                      )}
                    </Droppable>
                  </DragDropContext>
                </div>
              </div>
            ) : (
              <div className="fos-card p-6 text-center text-neutral-500">
                <ListChecks size={28} className="mx-auto mb-2" />
                <p className="text-sm">Select an SOP to see its steps.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {tab === "runs" && (
        <div>
          <h3 className="text-xs mono font-semibold mb-3 text-neutral-500">ACTIVE RUNS ({activeRuns.length})</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-8">
            {activeRuns.map((r) => (
              <RunCard key={r.id} run={r} sops={sops} onOpen={() => openRun(r)} />
            ))}
            {activeRuns.length === 0 && <p className="text-xs italic text-neutral-400">No active runs.</p>}
          </div>

          <h3 className="text-xs mono font-semibold mb-3 text-neutral-500">COMPLETED RUNS ({completedRuns.length})</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {completedRuns.map((r) => (
              <RunCard key={r.id} run={r} sops={sops} onOpen={() => openRun(r)} />
            ))}
          </div>
        </div>
      )}

      {runContext && <RunModal sop={runContext} onClose={() => setRunContext(null)} onDone={async () => { setRunContext(null); await load(); setTab("runs"); }} />}
      {selectedRun && (
        <RunDetail run={selectedRun} steps={runStepsData} onClose={() => setSelectedRun(null)} onSet={setStepStatus} onPause={pauseCancel} sops={sops} />
      )}
      {showCreate && (
        <CreateSopModal
          onClose={() => setShowCreate(false)}
          onCreated={async () => { setShowCreate(false); await load(); }}
        />
      )}
    </div>
  );
}

function TabBtn({ children, active, onClick, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
        active ? "border-neutral-900 text-neutral-900" : "border-transparent text-neutral-500 hover:text-neutral-900"
      }`}
    >
      {children}
    </button>
  );
}

function RunCard({ run, sops, onOpen }) {
  const sop = sops.find((s) => s.id === run.sop_id);
  return (
    <div className="fos-card fos-hover p-3 cursor-pointer" onClick={onOpen} data-testid={`run-card-${run.id}`}>
      <p className="text-sm font-semibold">{sop?.name || "—"}</p>
      <p className="text-xs mt-1 text-neutral-500">{run.context || "—"}</p>
      <div className="flex items-center justify-between mt-3 text-[10px] mono text-neutral-500">
        <span>{run.status.toUpperCase().replace("_", " ")}</span>
        <span>{formatDate(run.started_at)}</span>
      </div>
    </div>
  );
}

function RunModal({ sop, onClose, onDone }) {
  const [context, setContext] = useState(sop.name === "New Team Member Onboarding" ? "Onboarding …" : "");
  const [assignee, setAssignee] = useState("");
  const submit = async () => {
    if (!context.trim()) return toast.error("Add context");
    await endpoints.runSop(sop.id, { context, triggered_by: "user", assigned_to: assignee });
    toast.success("SOP run started");
    onDone();
  };
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center p-4 bg-black/40">
      <div className="fos-card p-5 w-full max-w-md" data-testid="run-modal">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold">Run: {sop.name}</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-neutral-100"><X size={16} /></button>
        </div>
        <input value={context} onChange={(e) => setContext(e.target.value)} placeholder="Context (e.g. Onboarding Rahul Mehta)" className="w-full text-sm px-3 py-2 rounded-md border border-neutral-200 bg-white outline-none mb-2" data-testid="run-context" />
        <input value={assignee} onChange={(e) => setAssignee(e.target.value)} placeholder="Assign steps to (optional)" className="w-full text-sm px-3 py-2 rounded-md border border-neutral-200 bg-white outline-none mb-3" />
        <button onClick={submit} className="w-full text-sm px-3 py-2 rounded-md font-medium bg-neutral-900 text-white hover:bg-neutral-800 transition-colors" data-testid="run-start">Start Run</button>
      </div>
    </div>
  );
}

function RunDetail({ run, steps, onClose, onSet, onPause, sops }) {
  const sop = sops.find((s) => s.id === run.sop_id);
  const done = steps.filter((s) => s.status === "done").length;
  const total = steps.length || 1;
  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-black/40" onClick={onClose}>
      <div className="w-full max-w-2xl h-full overflow-y-auto p-5 bg-white border-l border-neutral-200" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="font-semibold text-lg">{sop?.name}</h2>
            <p className="text-xs text-neutral-500">{run.context}</p>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-neutral-100"><X size={18} /></button>
        </div>

        <div className="fos-card p-3 mb-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs mono text-neutral-500">PROGRESS · {done}/{total}</p>
            <p className="text-xs mono font-semibold">{Math.round((done / total) * 100)}%</p>
          </div>
          <div className="h-2 rounded-full overflow-hidden bg-neutral-100">
            <div className="h-full rounded-full bg-neutral-900" style={{ width: `${(done / total) * 100}%` }} />
          </div>
          {run.status === "in_progress" && (
            <div className="flex items-center gap-2 mt-3">
              <button onClick={() => onPause(run, "paused")} className="text-[11px] px-2 py-1 rounded-md flex items-center gap-1 bg-amber-500 text-white hover:bg-amber-600 transition-colors">
                <Pause size={11} /> Pause
              </button>
              <button onClick={() => onPause(run, "cancelled")} className="text-[11px] px-2 py-1 rounded-md bg-red-500 text-white hover:bg-red-600 transition-colors">
                Cancel Run
              </button>
            </div>
          )}
        </div>

        <div className="space-y-2">
          {steps.map((st) => (
            <RunStepRow key={st.id} step={st} onSet={onSet} />
          ))}
        </div>
      </div>
    </div>
  );
}

function RunStepRow({ step, onSet }) {
  const isDone = step.status === "done";
  const isSkipped = step.status === "skipped";
  return (
    <div className="p-3 rounded-md fos-card">
      <div className="flex items-center gap-2 mb-1">
        <span className="text-[10px] mono px-1.5 py-0.5 rounded bg-neutral-100 text-neutral-500">
          {String(step.step_number).padStart(2, "0")}
        </span>
        <span className={`text-sm font-medium flex-1 ${isDone || isSkipped ? "opacity-60" : ""}`}>{step.title}</span>
        <span className="text-[10px] mono text-neutral-500">{step.status.toUpperCase()}</span>
      </div>
      {step.due_at && (
        <p className="text-[10px] mono mb-2 text-neutral-500">DUE {formatDate(step.due_at)}</p>
      )}
      <div className="flex items-center gap-1.5">
        <button
          onClick={() => onSet(step, "done")}
          className={`text-[11px] px-2 py-1 rounded-md flex items-center gap-1 transition-colors ${isDone ? "bg-neutral-200 text-neutral-500" : "bg-neutral-900 text-white hover:bg-neutral-800"}`}
          data-testid={`step-done-${step.id}`}
        >
          <Check size={11} /> Done
        </button>
        <button
          onClick={() => onSet(step, "in_progress")}
          className="text-[11px] px-2 py-1 rounded-md border border-neutral-200 text-neutral-800 hover:bg-neutral-50 transition-colors"
        >
          Start
        </button>
        <button
          onClick={() => onSet(step, "skipped")}
          className="text-[11px] px-2 py-1 rounded-md flex items-center gap-1 border border-neutral-200 text-neutral-500 hover:bg-neutral-50 transition-colors"
        >
          <SkipForward size={11} /> Skip
        </button>
      </div>
    </div>
  );
}

function CreateSopModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", description: "", category: "custom", trigger_keywords: "" });
  const [stepsText, setStepsText] = useState("");
  const submit = async () => {
    if (!form.name.trim()) return toast.error("Add a name");
    const kws = form.trigger_keywords.split(",").map((s) => s.trim()).filter(Boolean);
    const sop = await endpoints.createSop({ ...form, trigger_keywords: kws });
    const lines = stepsText.split("\n").map((l) => l.trim()).filter(Boolean);
    for (let i = 0; i < lines.length; i++) {
      await endpoints.createSopStep({ sop_id: sop.id, step_number: i + 1, title: lines[i] });
    }
    toast.success("SOP created");
    onCreated();
  };
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center p-4 bg-black/40">
      <div className="fos-card p-5 w-full max-w-lg">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold">Create SOP</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-neutral-100"><X size={16} /></button>
        </div>
        <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="SOP name" className="w-full text-sm px-3 py-2 rounded-md border border-neutral-200 bg-white outline-none mb-2" data-testid="sop-name" />
        <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description" className="w-full text-sm px-3 py-2 rounded-md border border-neutral-200 bg-white outline-none mb-2" />
        <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full text-sm px-3 py-2 rounded-md border border-neutral-200 bg-white outline-none mb-2">
          {["hr", "client", "ops", "finance", "legal", "custom"].map((c) => (<option key={c} value={c}>{c}</option>))}
        </select>
        <input value={form.trigger_keywords} onChange={(e) => setForm({ ...form, trigger_keywords: e.target.value })} placeholder="Trigger keywords (comma separated)" className="w-full text-sm px-3 py-2 rounded-md border border-neutral-200 bg-white outline-none mb-2" />
        <textarea value={stepsText} onChange={(e) => setStepsText(e.target.value)} placeholder="One step per line…" rows={5} className="w-full text-sm px-3 py-2 rounded-md border border-neutral-200 bg-white outline-none mb-3" data-testid="sop-steps" />
        <button onClick={submit} className="w-full text-sm px-3 py-2 rounded-md font-medium bg-neutral-900 text-white hover:bg-neutral-800 transition-colors" data-testid="sop-create-submit">Create SOP</button>
      </div>
    </div>
  );
}
